import React, { useEffect, useMemo, useRef, useState } from 'react';

import { StepsNavigationLegacy } from '@/widgets/ServiceFormTemplate';

import { ConditionView } from '@/shared/ui';

import { ServiceFeedback } from '@/components/Forms/ServiceFeedback';

import { SERVICE_CODES } from '@/lib/consts';

import { DocumentTypeCode } from '@/api/types';

import { allAllowedToChoosePassportDocumentTypeCodes } from '../../../../../components/Forms/constants';
import { HeaderWithHomeLinkButton } from '../../../../../components/Forms/HeaderWithHomeLinkButton';
import { ServiceInformer } from '../../../../../components/Forms/ServiceInformer';
import { ServiceInformerContainer } from '../../../../../components/Forms/ServiceInformer/ServiceInformerContainer';
import { ServiceInformerType } from '../../../../../components/Forms/ServiceInformer/types';
import { ServiceNames, StepsNavigationName } from '../../../../../config/services';
import { getDateWithoutTime } from '../../../../../helpers/dates';
import { stepsNavigationDataFactory } from '../../../../../helpers/stepsNavigationDataFactory';

import FormSteps from './Steps/StepInitial';

import './PermissionRenewal.scss';
import '../../../../../containers/StyleSettings/themes/default/layout.scss';

export const StepsNavigationData = stepsNavigationDataFactory([
  StepsNavigationName.serviceDescription,
  StepsNavigationName.permissionData,
  StepsNavigationName.personalAndPassport,
  StepsNavigationName.currentPlaceOfResidence,
  StepsNavigationName.personStatus,
  StepsNavigationName.health,
  StepsNavigationName.insurance,
  StepsNavigationName.technicalCondition,
  StepsNavigationName.additionalDocument,
  StepsNavigationName.confirmationOfApplication,
  StepsNavigationName.signUp,
  StepsNavigationName.payment,
  StepsNavigationName.result,
]);

export const StepsNavigationDataWithoutPayment = stepsNavigationDataFactory([
  StepsNavigationName.serviceDescription,
  StepsNavigationName.permissionData,
  StepsNavigationName.personalAndPassport,
  StepsNavigationName.currentPlaceOfResidence,
  StepsNavigationName.personStatus,
  StepsNavigationName.health,
  StepsNavigationName.insurance,
  StepsNavigationName.technicalCondition,
  StepsNavigationName.additionalDocument,
  StepsNavigationName.confirmationOfApplication,
  StepsNavigationName.signUp,
  StepsNavigationName.result,
]);

export const disallowedDocumentTypeCodesForAdditionalDocumentStep = [
  ...allAllowedToChoosePassportDocumentTypeCodes,
  DocumentTypeCode.MEDICAL_REFERENCE,
  DocumentTypeCode.TECHNICAL_CONDITION,
  DocumentTypeCode.CERTIFICATION,
  DocumentTypeCode.PAYMENT_REFERENCE,
  DocumentTypeCode.INSURANCE_POLICY,
];

export const currentServiceCode = SERVICE_CODES.VALIDITY_PERIOD_PROLONGATION;
export const serviceName = ServiceNames[currentServiceCode];
export const totalSteps = StepsNavigationData.length;
export const insuranceStepNumber = StepsNavigationData.find(
  (stepNavigation) => stepNavigation.name === StepsNavigationName.insurance,
)?.step;
export const stepNumberToPay = StepsNavigationData.length - 1;

export const todaysDate = getDateWithoutTime(new Date());

export const PermissionRenewal: React.FC = () => {
  const htmlRef = useRef<HTMLElement>(null);
  const [myObjects, setMyObjects] = useState(StepsNavigationData);
  const currentStep = useMemo(() => myObjects.find((step) => step.active), [myObjects]);
  const isLastStep: boolean = currentStep === myObjects[myObjects.length - 1];
  const [currentRequestId, setCurrentRequestId] = useState<string>('');
  const isPaymentDetailsInformerActive = useMemo(
    () => currentStep?.step === StepsNavigationData[0].step || currentStep?.step === stepNumberToPay,
    [currentStep],
  );
  const isClueInformerActive = useMemo(
    () => currentStep?.step === StepsNavigationData[0].step || currentStep?.step === StepsNavigationData.at(-1)?.step,
    [currentStep],
  );
  const isValidityPeriodViolationInformerActive = useMemo(() => currentStep?.step === 2, [currentStep]);
  const isPlaceOfResidenceInformerActive = useMemo(() => currentStep?.step === 4, [currentStep]);
  const isPersonsStatusInformerActive = useMemo(() => currentStep?.step === 5, [currentStep]);
  const isInsuranceDataInformerActive = useMemo(() => currentStep?.step === 7, [currentStep]);
  const isTechnicalConditionDetailsInformerActive = useMemo(() => currentStep?.step === 8, [currentStep]);
  const isTechnicalConditionDocumentDataInformerActive = useMemo(() => currentStep?.step === 8, [currentStep]);
  const isQrCodeInformerActive = useMemo(() => currentStep?.step === 11, [currentStep]);

  const handleHiddenStep = (code: string): void => {
    const permitCode = [SERVICE_CODES.PURCHASE, SERVICE_CODES.COLD_STEEL_TRANSPORTATION];
    const isCodeInPermitCode = permitCode.toString().includes(code);
    StepsNavigationData[6].hidden = isCodeInPermitCode;
  };

  useEffect(() => {
    const observer = new MutationObserver(() => {
      const stepToActivate = Number(htmlRef.current?.querySelector('div[data-step]')?.getAttribute('data-step'));

      setMyObjects((prevState) =>
        prevState.map((step) => {
          if (step.step === stepToActivate) {
            return { ...step, active: true, completed: false };
          } else {
            return {
              ...step,
              completed: step.step < stepToActivate || step.completed,
              active: false,
            };
          }
        }),
      );
    });
    htmlRef.current && observer.observe(htmlRef.current, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  const handleReceiveRequestId = (requestId: string): void => {
    setCurrentRequestId(requestId);
  };

  return (
    <section className="section-block-medium section-light">
      <div className="container container-with-aside_margin-bottom-sm">
        <HeaderWithHomeLinkButton header={serviceName} />
      </div>
      <div className="wpp-container container container-with-two-asides container-with-aside_gap-md">
        <aside className="container-with-aside__aside">
          <StepsNavigationLegacy navigationData={myObjects} />
        </aside>
        <div>
          <section
            className="container-with-aside__section container-with-aside__section_width-md max-width-md"
            ref={htmlRef}
          >
            <FormSteps
              setMyObjects={setMyObjects}
              handleHiddenStep={handleHiddenStep}
              steps={StepsNavigationData}
              onReceiveRequestId={handleReceiveRequestId}
            />
          </section>

          <ConditionView if={isLastStep && !!currentRequestId}>
            <ServiceFeedback requestId={currentRequestId} />
          </ConditionView>
        </div>
        <ServiceInformerContainer>
          {isQrCodeInformerActive && <ServiceInformer type={ServiceInformerType.QR_CODE1} />}
          {isPaymentDetailsInformerActive && (
            <ServiceInformer type={ServiceInformerType.PAYMENT_DETAILS} serviceCode={currentServiceCode} />
          )}
          {isClueInformerActive && <ServiceInformer type={ServiceInformerType.CLUE} />}
          {isValidityPeriodViolationInformerActive && (
            <ServiceInformer type={ServiceInformerType.VALIDITY_PERIOD_VIOLATION} />
          )}
          {isPlaceOfResidenceInformerActive && <ServiceInformer type={ServiceInformerType.PLACE_OF_RESIDENCE} />}
          {isPersonsStatusInformerActive && <ServiceInformer type={ServiceInformerType.PERSONS_STATUS} />}
          {isTechnicalConditionDetailsInformerActive && (
            <ServiceInformer type={ServiceInformerType.TECHNICAL_CONDITION_DETAILS} />
          )}
          {isTechnicalConditionDocumentDataInformerActive && (
            <ServiceInformer type={ServiceInformerType.TECHNICAL_CONDITION_DOCUMENT_DATA} />
          )}
          {isInsuranceDataInformerActive && <ServiceInformer type={ServiceInformerType.INSURANCE_DATA} />}
          <ServiceInformer type={ServiceInformerType.CONTACT_CENTER} />
        </ServiceInformerContainer>
      </div>
    </section>
  );
};
